export const isIpaType = (platformType) => platformType.match('^(ipa|other-online-events)$');

const SAFE_LINKS_HOST_REGEX = /(^|\.)safelinks\.protection\.outlook\.com$/i;
const TEAMS_HOST_REGEX = /^(?:www\.)?(teams\.(?:microsoft|live)\.com|teams\.cloud\.microsoft)$/i;

const unwrapSafeLink = (link) => {
  try {
    const parsed = new URL(link);
    if (!SAFE_LINKS_HOST_REGEX.test(parsed.hostname)) return link;
    return parsed.searchParams.get('url') || link;
  } catch (_e) {
    return link;
  }
};

export const normalizeTeamsMeetingLink = (value) => {
  if (typeof value !== 'string') return value;

  let link = value.replace(/[\u200B-\u200D\u2060\uFEFF]/g, '').trim().replace(/^[<"']+|[>"']+$/g, '').replace(/[),.;]+$/, '');
  if (!link) return link;

  if (!/^[a-z][a-z\d+.-]*:\/\//i.test(link)) {
    link = `https://${link}`;
  }
  link = unwrapSafeLink(link.replace(/^http:\/\//i, 'https://'));

  try {
    const parsed = new URL(link);
    const hostMatch = parsed.hostname.match(TEAMS_HOST_REGEX);
    if (!hostMatch) return value.trim();

    const host = hostMatch[1].toLowerCase();
    const hashPath = parsed.hash.replace(/^#/, '');
    const launcherPath = parsed.pathname === '/dl/launcher/launcher.html'
      ? decodeURIComponent(parsed.searchParams.get('url') || '').replace(/^\/_#/, '')
      : '';

    let rebuilt;
    if (/^\/(?:_|v2\/?)?$/.test(parsed.pathname) && /^\/(l|meet)\//.test(hashPath)) {
      rebuilt = new URL(`https://${host}${hashPath}`);
    } else if (launcherPath.startsWith('/l/')) {
      rebuilt = new URL(`https://${host}${launcherPath}`);
    } else {
      rebuilt = new URL(`https://${host}${parsed.pathname}${parsed.search}${parsed.hash}`);
    }

    return rebuilt.href.replace(/'/g, '%27');
  } catch (_e) {
    return value.trim();
  }
};

/**
  * Zoom regex to match the following URLs:
  * https://zoom.us/j/1234567890
  * https://us02web.zoom.us/w/1234567890?pwd=123
  * https://previewfreemovies.zoom.us/w/1234567890?pwd=123
  * https://previewfreemovies.zoom.us/s/9386861875?zak=eyJ0eXAiOiJKV1QiLCJzdiI6IjAwMDAwMSIsInptX3NrbSI6InptX28ybSIsImFsZyI6IkhTMjU2In0.eyJpc3MiOiJ3ZWIiLCJjbHQiOjAsIm1udW0iOiI5Mzg2ODYxODc1IiwiYXVkIjoiY2xpZW50c20iLCJ1aWQiOiJGSlptVUZVcVE4MmFkQzhXd256b0Z3IiwiemlkIjoiMGI3YjVjMGQ0NmY4NDQzNDg3YjFjZTk0NWMwYjMwNGEiLCJzayI6IjAiLCJzdHkiOjEwMCwid2NkIjoidXMwNiIsImV4cCI6MTc0Mjg1MDQ0NywiaWF0IjoxNzQyODQzMjQ3LCJhaWQiOiJkbkJWS1VwN1RVeVFFdHYzazV2UWJBIiwiY2lkIjoiIn0.CRDceFdfmHpMCz1BARb59Sstwv74Uwd7hwkiyc_dnww",
  * https://zoom.us/my/john-doe
  * https://zoom.us/my/jane123?tk=abc
 */
export const ZOOM_JOIN_MEETING_URL_REGEX = /https:\/\/[\w.-]*zoom\.us\/(j|s|w|my)\/([\w-]{1,40})(\?[^\s]*)?/i;

/**
 * The `my` arm above matches a personal room link, whose path segment is a vanity slug
 * (`/my/john-doe`), NOT a meeting number. That is deliberate — the form has to accept the
 * link so the backend can resolve it to the room's real join URL on save
 * (functions/resources/platforms/zoom/helpers/meeting-link.js). But the slug must never
 * reach a join: Zoom rejects a signature minted over it with error 3712.
 *
 * So anywhere a meeting *number* is needed, extract it with this instead of reading a path
 * segment. Mirrors `parseZoomMeetingId` on the backend — keep the two in step.
 */
export const parseZoomMeetingNumber = (url) => {
  const str = String(url || '');
  const fromPath = str.match(/\/(?:j|w|s)\/(\d+)/);
  if (fromPath) return fromPath[1];
  const fromQuery = str.match(/[?&]confno=(\d+)/);
  return fromQuery ? fromQuery[1] : '';
};
export const MEET_JOIN_MEETING_URL_REGEX = /^[https://]*meet.google.com\/(?:meet\/)?[a-z]{3}-[a-z]{4}-[a-z]{3}.*/i;
export const VIMEO_JOIN_MEETING_URL_REGEX = /^https:\/\/vimeo\.com\/(event|webinars\/events)\/[\w-]+(?:\/[\w-]+)?$/i;
export const WEBEX_JOIN_MEETING_URL_REGEX = /^https:\/\/(?:\w+-by\.)?webex\.com\/wbxmjs\/joinservice\/sites\/(?:\w+\/)?meeting\/download\/[a-zA-Z\d]{32}(?:\?.*)?$|https:\/\/\w+\.webex\.com\/wbxmjs\/joinservice\/sites\/\w+\/meeting\/download\/[a-zA-Z\d]{32}(?:\?.*)?$/i;
// Mirrors the extension's canonical LinkedIn matcher (extension/js/platforms/linkedin.ts
// and livestream-url.service.js): the event theater page and the live video post page
// (linkedin.com/video/live/urn:li:ugcPost:<id>/). Keep in sync so the form accepts
// every URL the extension can actually read.
export const LINKEDIN_JOIN_MEETING_URL_REGEX = /https:\/\/www\.linkedin\.com\/(events\/([a-zA-Z0-9]+)\/theater\/|video\/live\/urn:li:ugcPost:[0-9]+\/?)/i;
export const STREAMYARD_JOIN_MEETING_URL_REGEX = /https:\/\/streamyard\.com\/(?!teams|plan)[a-zA-Z0-9-]+/i;
// Mirrors the extension's canonical Restream matcher (extension/js/platforms/restream.ts
// and livestream-url.service.js): studio link, the standalone chat embed, and the
// app.restream.io events/shows pages. Keep these in sync so the form accepts every
// URL the extension can actually read.
export const RESTREAM_JOIN_MEETING_URL_REGEX = /^https:\/\/studio\.restream\.io\/[a-zA-Z0-9-]+|^https:\/\/chat\.restream\.io\/embed\?token=[a-f0-9-]+|^https:\/\/app\.restream\.io\/events\/[0-9a-fA-F-]{36}|^https:\/\/app\.restream\.io\/shows\/[a-f0-9-]+$/i;
export const AIRMEET_JOIN_MEETING_URL_REGEX = /^https:\/\/www\.airmeet\.com\/event\/[a-zA-Z0-9-]+$/i;
export const KICK_JOIN_MEETING_URL_REGEX = /^https:\/\/kick\.com\/[a-zA-Z0-9-]+$/i;
// Mirrors the extension's canonical Substack matcher (extension/js/platforms/substack.ts
// and livestream-url.service.js): the host live-stream studio
// (`{publication}.substack.com/publish/live-stream/{id}`) and the viewer/share
// link (`/live-stream/{id}`, on the publication host or substack.com). The
// numeric id rejects `/publish/live-stream/new`, the create screen.
export const SUBSTACK_JOIN_MEETING_URL_REGEX = /^https:\/\/(?:[a-zA-Z0-9-]+\.)?substack\.com\/(?:publish\/)?live-stream\/\d+(?:[/?#].*)?$/i;
export const TEAMS_WEB_BOT_JOIN_MEETING_URL_REGEX = /^https:\/\/(?:teams\.(?:microsoft|live)\.com|teams\.cloud\.microsoft)\/(l|dl|meet|meetup-join)\/[^\s"']+$/i;
// Mirrors the extension's canonical Adobe Connect matcher (livestream-url.service.js).
export const ADOBE_CONNECT_JOIN_MEETING_URL_REGEX = /^https:\/\/[a-zA-Z0-9-]+\.adobeconnect\.com\/[a-zA-Z0-9-]+\/?(?:\?.*)?$/i;
